import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useConfig } from '@/context/ConfigContext';
import { resolveAssetUrl } from '@/utils/assetUrl';
import { TaleemXMark, TaleemXWordmark, isDefaultBranding } from '@/components/brand';

// Icons as components
const OverviewIcon = () => (
  <svg style={{ width: '24px', height: '24px' }} className="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" />
  </svg>
);

const AttendanceIcon = () => (
  <svg style={{ width: '24px', height: '24px' }} className="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const ReportsIcon = () => (
  <svg style={{ width: '24px', height: '24px' }} className="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
  </svg>
);

const RecordsIcon = () => (
  <svg style={{ width: '24px', height: '24px' }} className="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
  </svg>
);

const TodayIcon = () => (
  <svg style={{ width: '24px', height: '24px' }} className="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
  </svg>
);

const HistoryIcon = () => (
  <svg style={{ width: '24px', height: '24px' }} className="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const StudentsIcon = () => (
  <svg style={{ width: '24px', height: '24px' }} className="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
  </svg>
);

const ProfileIcon = () => (
  <svg style={{ width: '24px', height: '24px' }} className="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
  </svg>
);

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    className={clsx('h-4 w-4 shrink-0 transition-transform duration-200', isOpen && 'rotate-180')}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 9l-7 7-7-7" />
  </svg>
);

type NavChild = {
  to: string;
  label: string;
  children?: { to: string; label: string }[];
};

type NavItem = {
  to?: string;
  label: string;
  icon: React.ComponentType;
  children?: NavChild[];
};

const childPathMatches = (pathname: string, child: NavChild) =>
  pathname === child.to || Boolean(child.children?.some((nested) => pathname === nested.to));

const adminLinks: NavItem[] = [
  { to: '/admin', label: 'Overview', icon: OverviewIcon },
  {
    label: 'Attendance',
    icon: AttendanceIcon,
    children: [
      { to: '/admin/attendance/students', label: 'Students Attendance' },
      { to: '/admin/attendance/teachers', label: 'Teachers Attendance' },
    ],
  },
  {
    label: 'Reports',
    icon: ReportsIcon,
    children: [
      { to: '/admin/reports/students', label: 'Students Reports' },
      { to: '/admin/reports/teachers', label: 'Teachers Reports' },
    ],
  },
  {
    label: 'Records',
    icon: RecordsIcon,
    children: [
      {
        to: '/admin/students',
        label: 'Students Records',
        children: [{ to: '/admin/students/class-transfer', label: 'Class assignment' }],
      },
      {
        to: '/admin/teachers',
        label: 'Teachers Records',
      },
    ],
  },
  {
    label: 'Settings',
    icon: RecordsIcon,
    children: [
      { to: '/admin/settings/holidays', label: 'Holidays & Vacations' },
      { to: '/admin/settings/school-structure', label: 'School Structure' },
    ],
  },
];

const teacherLinks: NavItem[] = [
  { to: '/teacher', label: "Today's Attendance", icon: TodayIcon },
  { to: '/teacher/history', label: 'History', icon: HistoryIcon },
  { to: '/teacher/students', label: 'Students', icon: StudentsIcon },
  { to: '/teacher/profile', label: 'My Profile', icon: ProfileIcon },
];

type SidebarProps = {
  role: 'admin' | 'teacher';
  isOpen?: boolean;
  isCollapsed?: boolean;
  onClose?: () => void;
};

export function Sidebar({ role, isOpen = false, isCollapsed = false, onClose }: SidebarProps) {
  const location = useLocation();
  const { config } = useConfig();
  const links = role === 'admin' ? adminLinks : teacherLinks;

  // Track which expandable menus are open
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(() => {
    // Auto-expand menu if current path matches a child
    const expanded = new Set<string>();
    // Iterate securely
    (role === 'admin' ? adminLinks : teacherLinks).forEach((link) => {
      if (link.children) {
        const isChildActive = link.children.some((child) => childPathMatches(location.pathname, child));
        if (isChildActive) {
          expanded.add(link.label);
        }
      }
    });
    return expanded;
  });

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const renderLink = (link: NavItem, collapsed: boolean) => {
    const Icon = link.icon;

    // If the link has children, render as expandable menu
    if (link.children) {
      const isExpanded = expandedMenus.has(link.label);
      const isChildActive = link.children.some((child) => childPathMatches(location.pathname, child));

      return (
        <li key={link.label}>
          <button
            type="button"
            onClick={() => toggleMenu(link.label)}
            title={collapsed ? link.label : undefined}
            className={clsx(
              'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              collapsed ? 'justify-center px-2' : 'justify-between',
              isChildActive
                ? 'bg-brand/10 text-brand'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
            )}
          >
            <span className="flex items-center gap-3">
              <Icon />
              {!collapsed && <span className="truncate">{link.label}</span>}
            </span>
            {!collapsed && <ChevronIcon isOpen={isExpanded} />}
          </button>
          {/* Children */}
          {!collapsed && isExpanded && (
            <ul className="mt-1 ml-4 space-y-1 border-l border-slate-200 pl-4">
              {link.children.map((child) => {
                const isActive = location.pathname === child.to;
                return (
                  <li key={child.to}>
                    <NavLink
                      to={child.to}
                      onClick={onClose}
                      className={clsx(
                        'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-brand text-white shadow-sm'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                      )}
                    >
                      <span className="truncate">{child.label}</span>
                    </NavLink>
                    {child.children?.length ? (
                      <ul className="mt-1 ml-3 space-y-1 border-l border-slate-200 pl-3">
                        {child.children.map((nested) => {
                          const nestedActive = location.pathname === nested.to;
                          return (
                            <li key={nested.to}>
                              <NavLink
                                to={nested.to}
                                onClick={onClose}
                                className={clsx(
                                  'flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                                  nestedActive
                                    ? 'bg-brand text-white shadow-sm'
                                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900',
                                )}
                              >
                                <span className="truncate">{nested.label}</span>
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      );
    }

    // Regular link without children
    const isActive = location.pathname === link.to;

    return (
      <li key={link.to}>
        <NavLink
          to={link.to!}
          title={collapsed ? link.label : undefined}
          className={clsx(
            'flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold transition-colors',
            collapsed ? 'justify-center px-2' : '',
            isActive
              ? role === 'teacher'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-brand text-white shadow-sm'
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
          )}
          onClick={onClose}
        >
          <Icon />
          {!collapsed && <span className="truncate">{link.label}</span>}
        </NavLink>
      </li>
    );
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={clsx(
          'hidden shrink-0 border-r border-slate-200 bg-white transition-all duration-200 lg:flex lg:flex-col',
          isCollapsed ? 'w-20' : 'w-72'
        )}
      >
        <div
          className={clsx(
            'flex h-20 items-center border-b border-slate-200',
            isCollapsed ? 'justify-center px-0' : 'px-6',
          )}
        >
          {isCollapsed ? (
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-50 overflow-hidden">
              {isDefaultBranding(config) ? (
                <TaleemXMark size="sm" className="h-6 w-6" />
              ) : (
                <img
                  src={resolveAssetUrl(config?.school_logo_url) || '/logo-mark.svg'}
                  alt={config?.school_name || 'School Logo'}
                  className="h-6 w-6 object-contain"
                />
              )}
            </div>
          ) : isDefaultBranding(config) ? (
            <div className="flex items-center">
              <TaleemXWordmark size="lg" tone="brand" />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-50 overflow-hidden">
                <img
                  src={resolveAssetUrl(config?.school_logo_url) || undefined}
                  alt={config?.school_name || 'School Logo'}
                  className="h-6 w-6 object-contain"
                />
              </div>
              <span className="text-lg font-semibold text-brand truncate">
                {config?.school_name}
              </span>
            </div>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-6">
          <ul className="space-y-1">
            {links.map((link) => renderLink(link, isCollapsed))}
          </ul>
        </nav>
        <div className={clsx("p-4 border-t border-slate-200 text-xs text-slate-400 font-mono", isCollapsed ? "text-center" : "text-center")}>
          v1.2.1
        </div>
      </aside>

      {/* Mobile sidebar (slide-over) */}
      <div className={clsx('lg:hidden', isOpen ? 'pointer-events-auto' : 'pointer-events-none')}>
        <div
          className={clsx(
            'fixed inset-0 z-40 bg-slate-900/40 transition-opacity',
            isOpen ? 'opacity-100' : 'opacity-0',
          )}
          onClick={onClose}
        />
        <aside
          className={clsx(
            'fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-lg transition-transform',
            isOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex h-20 items-center justify-between border-b border-slate-200 px-6">
            {isDefaultBranding(config) ? (
              <div className="flex items-center">
                <TaleemXWordmark size="lg" tone="brand" />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-50 overflow-hidden">
                  <img
                    src={resolveAssetUrl(config?.school_logo_url) || undefined}
                    alt={config?.school_name || 'School Logo'}
                    className="h-6 w-6 object-contain"
                  />
                </div>
                <span className="text-xl font-semibold text-brand truncate">
                  {config?.school_name}
                </span>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded p-2 text-slate-500 transition hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              aria-label="Close navigation"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav className="px-4 py-6">
            <ul className="space-y-1">
              {links.map((link) => renderLink(link, false))}
            </ul>
          </nav>
        </aside>
      </div>
    </>
  );
}
